import { useMutation, useQuery } from "@tanstack/react-query";

import { get, post, put } from "./client";
import { isApiError } from "./errors";

// ── Response envelope ──────────────────────────────────────────────────────
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  traceId?: string;
  errors?: Record<string, string[]>;
  timestamp: string;
}

// ── Lookup types ───────────────────────────────────────────────────────────
export interface LookupItem {
  id: number;
  code: string;
  name: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
}

export interface SectorLookup extends LookupItem {
  industryId: number;
}

export interface CurrencyLookup extends LookupItem {
  symbol: string;
}

export interface CountryLookup extends LookupItem {
  isoCode?: string;
}

// ── Deal detail lookup types ────────────────────────────────────────────────

/** One checklist question returned by GET /api/deal/detail-lookups. */
export interface DealDetailLookupItem {
  id: number;
  code: string;
  label: string;
  sortOrder: number;
}

/** One checklist answer returned as part of DealDto. */
export interface DealDetailDto {
  id: number;
  dealDetailLookupId: number;
  code: string;
  label: string;
  isAnswered: boolean;
}

/** One checklist answer sent to POST/PUT /api/deal. */
export interface SaveDealDetailItem {
  dealDetailLookupId: number;
  isAnswered: boolean;
}

// ── Deal DTO ───────────────────────────────────────────────────────────────
export interface DealDto {
  id: number;
  dealName: string;
  clientName?: string;
  targetName?: string;
  acquirerName?: string;
  pwcEngagementLead?: string;
  dealTypeId?: number;
  dealTypeName?: string;
  dealPhaseId?: number;
  dealPhaseName?: string;
  industryId?: number;
  industryName?: string;
  sectorId?: number;
  sectorName?: string;
  currencyId?: number;
  currencyName?: string;
  currencySymbol?: string;
  dealValueAmountId?: number;
  dealValueAmountName?: string;
  acquisitionTypeId?: number;
  acquisitionTypeName?: string;
  regulatoryApprovalIds: number[];
  buyerCompanyLegalStructureId?: number;
  buyerCompanyLegalStructureName?: string;
  targetCompanyLegalStructureId?: number;
  targetCompanyLegalStructureName?: string;
  /** Level of access granted to the deal team before close (optional). */
  levelOfAccessPreCloseId?: number;
  levelOfAccessPreCloseName?: string;
  /** Country ids the buyer currently operates in. */
  buyerCountryIds: number[];
  /** Country ids the target currently operates in. */
  targetCountryIds: number[];
  /** Buyer country ids in scope of the integration (optional). */
  buyerCountryScopeIntegrationIds?: number[];
  /** Target country ids in scope of the integration (optional). */
  targetCountryScopeIntegrationIds?: number[];
  geography?: string;
  acquiringBusiness?: string;
  numberOfEmployees?: number;
  isRegulatoryApprovalRequired: boolean;
  status: string;
  signingDate?: string;
  closeDate?: string;
  day1Date?: string;
  targetCompletionDate?: string;
  /** Simulated "today" date (YYYY-MM-DD). When set, overrides the real system date for prioritisation. */
  systemDate?: string;
  description?: string;
  /** Free-text entry point / pre-work narrative from Step 3. */
  entryPointAndPrework?: string;
  /** Checklist answers for Step 3. Empty array when none saved yet. */
  dealDetails: DealDetailDto[];
  createdDate: string;
  createdBy: string;
  modifiedDate?: string;
  modifiedBy?: string;
}

export interface CreateOrUpdateDealRequest {
  dealName?: string;
  clientName?: string;
  targetName?: string;
  acquirerName?: string;
  pwcEngagementLead?: string;
  dealTypeId?: number;
  dealPhaseId?: number;
  industryId?: number;
  sectorId?: number;
  currencyId?: number;
  dealValueAmountId?: number;
  acquisitionTypeId?: number;
  /** Omit to preserve existing; `[]` = clear all; non-empty = full replace. */
  regulatoryApprovalIds?: number[];
  buyerCompanyLegalStructureId?: number;
  targetCompanyLegalStructureId?: number;
  /** Level of access granted to the deal team before close (Step 3, optional). */
  levelOfAccessPreCloseId?: number;
  /** Omit to preserve existing; `[]` = clear all; non-empty = full replace. */
  buyerCountryIds?: number[];
  /** Omit to preserve existing; `[]` = clear all; non-empty = full replace. */
  targetCountryIds?: number[];
  /** Buyer countries in scope of the integration. Omit to preserve existing; non-empty = full replace. */
  buyerCountryScopeIntegrationIds?: number[];
  /** Target countries in scope of the integration. Omit to preserve existing; non-empty = full replace. */
  targetCountryScopeIntegrationIds?: number[];
  geography?: string;
  acquiringBusiness?: string;
  numberOfEmployees?: number;
  isRegulatoryApprovalRequired?: boolean;
  /** DateOnly string: "YYYY-MM-DD" */
  signingDate?: string;
  /** DateOnly string: "YYYY-MM-DD" */
  closeDate?: string;
  /** DateOnly string: "YYYY-MM-DD" */
  day1Date?: string;
  /** DateOnly string: "YYYY-MM-DD" */
  targetCompletionDate?: string;
  /** DateOnly string: "YYYY-MM-DD". Simulated "today" date. Omit to preserve existing. */
  systemDate?: string;
  /** Set to `true` to clear the simulated today override. Takes precedence over `systemDate`. */
  clearSystemDate?: boolean;
  description?: string;
  /** Free-text entry point / pre-work narrative from Step 3. Omit to preserve existing value. */
  entryPointAndPrework?: string;
  /** Omit to preserve existing; `[]` = clear all; non-empty = full replace. */
  dealDetails?: SaveDealDetailItem[];
}

// ── Query keys ─────────────────────────────────────────────────────────────
export const dealQueryKeys = {
  deal: ["api", "deal"] as const,
  dealTypes: ["api", "deal-types"] as const,
  dealPhases: ["api", "deal-phases"] as const,
  industries: ["api", "industries"] as const,
  sectors: (industryId?: number) => ["api", "sectors", industryId] as const,
  currencies: ["api", "currencies"] as const,
  companyLegalStructures: ["api", "company-legal-structures"] as const,
  dealValueAmounts: ["api", "deal-value-amounts"] as const,
  acquisitionTypes: ["api", "acquisition-types"] as const,
  regulatoryApprovals: ["api", "regulatory-approvals"] as const,
  countries: ["api", "countries"] as const,
  workstreams: ["api", "workstreams"] as const,
  dealDetailLookups: ["api", "deal", "detail-lookups"] as const,
  levelOfAccessPreClose: ["api", "level-of-access-pre-close"] as const,
};

// ── Lookup query hooks ─────────────────────────────────────────────────────

/** Fetches deal types (e.g. Acquisition, Merger). Cached for 10 minutes. */
export function useDealTypes() {
  return useQuery({
    queryKey: dealQueryKeys.dealTypes,
    queryFn: () => get<ApiResponse<LookupItem[]>>("/deal-types"),
    staleTime: 10 * 60 * 1000,
    select: (res) => res.data ?? [],
  });
}

/** Fetches deal phases (e.g. Pre-announcement, Signed). Cached for 10 minutes. */
export function useDealPhases() {
  return useQuery({
    queryKey: dealQueryKeys.dealPhases,
    queryFn: () => get<ApiResponse<LookupItem[]>>("/deal-phases"),
    staleTime: 10 * 60 * 1000,
    select: (res) => res.data ?? [],
  });
}

/** Fetches industries. Cached for 10 minutes. */
export function useIndustries() {
  return useQuery({
    queryKey: dealQueryKeys.industries,
    queryFn: () => get<ApiResponse<LookupItem[]>>("/industries"),
    staleTime: 10 * 60 * 1000,
    select: (res) => res.data ?? [],
  });
}

/** Fetches currencies. Cached for 10 minutes. */
export function useCurrencies() {
  return useQuery({
    queryKey: dealQueryKeys.currencies,
    queryFn: () => get<ApiResponse<CurrencyLookup[]>>("/currencies"),
    staleTime: 10 * 60 * 1000,
    select: (res) => res.data ?? [],
  });
}

/** Fetches company legal structures (buyer/target dropdowns). Cached for 10 minutes. */
export function useCompanyLegalStructures() {
  return useQuery({
    queryKey: dealQueryKeys.companyLegalStructures,
    queryFn: () => get<ApiResponse<LookupItem[]>>("/company-legal-structures"),
    staleTime: 10 * 60 * 1000,
    select: (res) => res.data ?? [],
  });
}

/** Fetches deal value amount bands. Cached for 10 minutes. */
export function useDealValueAmounts() {
  return useQuery({
    queryKey: dealQueryKeys.dealValueAmounts,
    queryFn: () => get<ApiResponse<LookupItem[]>>("/deal-value-amounts"),
    staleTime: 10 * 60 * 1000,
    select: (res) => res.data ?? [],
  });
}

/** Fetches acquisition types. Cached for 10 minutes. */
export function useAcquisitionTypes() {
  return useQuery({
    queryKey: dealQueryKeys.acquisitionTypes,
    queryFn: () => get<ApiResponse<LookupItem[]>>("/acquisition-types"),
    staleTime: 10 * 60 * 1000,
    select: (res) => res.data ?? [],
  });
}

/** Fetches regulatory approvals. Cached for 10 minutes. */
export function useRegulatoryApprovals() {
  return useQuery({
    queryKey: dealQueryKeys.regulatoryApprovals,
    queryFn: () => get<ApiResponse<LookupItem[]>>("/regulatory-approvals"),
    staleTime: 10 * 60 * 1000,
    select: (res) => res.data ?? [],
  });
}

/** Fetches countries (buyer/target multi-select). Cached for 10 minutes. */
export function useCountries() {
  return useQuery({
    queryKey: dealQueryKeys.countries,
    queryFn: () => get<ApiResponse<CountryLookup[]>>("/countries"),
    staleTime: 10 * 60 * 1000,
    select: (res) => res.data ?? [],
  });
}

// ── Workstreams ────────────────────────────────────────────────────────────

export interface WorkstreamItem {
  id: number;
  code: string;
  name: string;
}

/** GET /api/workstreams – fetches workstreams for dropdowns. Cached 10 min. */
export function useWorkstreams() {
  return useQuery({
    queryKey: dealQueryKeys.workstreams,
    queryFn: () => get<ApiResponse<WorkstreamItem[]>>("/workstreams"),
    staleTime: 10 * 60 * 1000,
    select: (res) => res.data ?? [],
  });
}

/** GET /api/deal/detail-lookups – ordered checklist questions for Step 3. Stable data; cached 30 minutes. */
export function useDealDetailLookups() {
  return useQuery({
    queryKey: dealQueryKeys.dealDetailLookups,
    queryFn: () => get<ApiResponse<DealDetailLookupItem[]>>("/deal/detail-lookups"),
    staleTime: 30 * 60 * 1000,
    select: (res) => res.data ?? [],
  });
}

/** GET /api/level-of-access-pre-close – optional Step 3 dropdown options. Cached for 10 minutes. */
export function useLevelOfAccessPreClose() {
  return useQuery({
    queryKey: dealQueryKeys.levelOfAccessPreClose,
    queryFn: () => get<ApiResponse<LookupItem[]>>("/level-of-access-pre-close"),
    staleTime: 10 * 60 * 1000,
    select: (res) => res.data ?? [],
  });
}

function getStatus(error: unknown): number | null {
  if (isApiError(error)) {
    return typeof error.status === "number" ? error.status : null;
  }
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === "number" ? status : null;
}

function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }
  const responseData = (error as { response?: { data?: unknown } })?.response?.data;
  if (responseData && typeof responseData === "object") {
    const record = responseData as { message?: unknown; detail?: unknown; error?: unknown };
    if (typeof record.message === "string") {
      return record.message;
    }
    if (typeof record.detail === "string") {
      return record.detail;
    }
    if (typeof record.error === "string") {
      return record.error;
    }
  }
  const message = (error as { message?: unknown })?.message;
  return typeof message === "string" ? message : "";
}

export function isNoDealApiError(error: unknown): boolean {
  const status = getStatus(error);
  if (status === 404) {
    return true;
  }

  const message = getErrorMessage(error).trim().toLowerCase();
  return message.includes("no deal exists") || message.includes("no deal is currently active");
}

/**
 * GET /api/deal – fetches the current deal.
 * Returns null when onboarding has not started yet (404 or equivalent no-deal payload).
 */
export function useDeal({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: dealQueryKeys.deal,
    queryFn: async () => {
      try {
        return await get<ApiResponse<DealDto | null>>("/deal");
      } catch (err) {
        if (isNoDealApiError(err)) return null;
        throw err;
      }
    },
    select: (res) => res?.data ?? null,
    enabled,
    retry: false,
  });
}

// ── Deal mutation ──────────────────────────────────────────────────────────

/** POST /api/deal/submit – promotes the draft deal to active. Returns the updated deal. */
export function useSubmitDeal() {
  return useMutation({
    mutationFn: () => post<ApiResponse<DealDto>>("/deal/submit", {}),
  });
}

/** POST /api/deal – creates a new draft or patches an existing draft. */
export function useCreateOrUpdateDeal() {
  return useMutation({
    mutationFn: (request: CreateOrUpdateDealRequest) =>
      post<ApiResponse<DealDto>>("/deal", request),
  });
}

/** PUT /api/deal – updates the current deal without going through create guards. */
export function useUpdateDeal() {
  return useMutation({
    mutationFn: (request: CreateOrUpdateDealRequest) =>
      put<ApiResponse<DealDto>>("/deal", request),
  });
}
